// ASTRA R2: campaign-first work area using the canonical Panel, Chip, SearchField
// and Skeleton patterns. Live counts and reference volumes remain distinct.
import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, RefreshCw } from "lucide-react";
import { isApiError } from "@/lib/api";
import { EmptyState, ErrorState } from "@/components/States";
import { Button } from "@/components/ui/button";
import { Chip } from "@/components/ui/chip";
import { Select } from "@/components/ui/input";
import { SearchField } from "@/components/ui/search-field";
import { Skeleton } from "@/components/ui/skeleton";
import { campaignReferenceForCode, normaliseCampaignKey } from "@/data/campaignReference";
import type { RadarCampaign } from "@/lib/types.radar";
import { useRadarCampaigns } from "./useRadar";

function formatCounter(value: number | null | undefined): string {
  return value == null ? "Not available" : value.toLocaleString("en-GB");
}

function CampaignCard({ campaign }: { campaign: RadarCampaign }) {
  const reference = campaignReferenceForCode(campaign.code);
  const key = campaign.code === null ? campaign.id : normaliseCampaignKey(campaign.code);
  const detailTo = `/high-intent/campaigns/${encodeURIComponent(campaign.code ?? campaign.id)}`;

  return (
    <Link
      to={detailTo}
      className="inv-card group flex h-full min-h-11 flex-col overflow-hidden transition-colors hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 motion-reduce:transition-none"
    >
      <div className="flex flex-1 flex-col gap-4 p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="type-app-strong text-base font-semibold text-ink">
              {campaign.name ?? campaign.code ?? campaign.id}
            </h3>
            <p className="inv-mono mt-1 break-words text-xs text-ink-3">{key}</p>
          </div>
          <Chip signal={campaign.status === "active" ? "buy" : undefined} dot={campaign.status === "active"}>
            {campaign.status}
          </Chip>
        </div>
        <p className="text-sm leading-relaxed text-ink-2">
          {reference?.overview ?? "Campaign brief not yet available. Open the campaign to review its live results."}
        </p>
        <dl className="mt-auto grid grid-cols-2 gap-4 border-y border-line py-4">
          <div>
            <dt className="inv-label">Accepted leads</dt>
            <dd className={`inv-mono mt-2 font-semibold text-ink ${campaign.counters.leads == null ? "text-sm" : "text-xl"}`}>{formatCounter(campaign.counters.leads)}</dd>
          </div>
          <div>
            <dt className="inv-label">High intent</dt>
            <dd className={`inv-mono mt-2 font-semibold text-ink ${campaign.counters.high_intent == null ? "text-sm" : "text-xl"}`}>{formatCounter(campaign.counters.high_intent)}</dd>
          </div>
        </dl>
        <p className="text-xs leading-relaxed text-ink-3">High intent is a scored subset of accepted leads.</p>
        {reference ? (
          <div className="flex flex-col gap-2 text-xs text-ink-3">
            <p><span className="font-medium text-ink-2">Territory </span>{reference.reach}</p>
            <p><span className="font-medium text-ink-2">Volume on record </span>
              {reference.volume.validLeads.toLocaleString("en-GB")} valid leads ·{" "}
              {reference.volume.reIntentEvents.toLocaleString("en-GB")} re-intent events
            </p>
          </div>
        ) : (
          <p className="text-xs text-ink-3">No Campaign Reference entry for {campaign.code ?? campaign.id}.</p>
        )}
      </div>
      <div className="flex min-h-11 items-center justify-between gap-2 border-t border-line bg-surface-muted px-5 py-3 text-sm font-medium text-primary">
        <span>Open campaign</span>
        <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </div>
    </Link>
  );
}

function CampaignBoardSkeleton() {
  return (
    <div role="status" aria-label="Loading campaigns…" className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      {Array.from({ length: 6 }, (_, index) => (
        <div key={index} className="inv-card flex flex-col gap-5 p-5" aria-hidden="true">
          <Skeleton className="h-6 w-2/3" />
          <Skeleton className="h-10 w-full" />
          <div className="grid grid-cols-2 gap-4"><Skeleton className="h-16" /><Skeleton className="h-16" /></div>
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-11 w-full" />
        </div>
      ))}
    </div>
  );
}

export function CampaignsBoard() {
  const campaigns = useRadarCampaigns();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const denied = campaigns.isError && isApiError(campaigns.error) && campaigns.error.httpStatus === 403;
  const rows = campaigns.isSuccess ? campaigns.data.campaigns : [];
  const wanted = search.trim().toLocaleLowerCase("en-GB");
  const visible = rows.filter((campaign) => {
    const text = `${campaign.name ?? ""} ${campaign.code ?? ""} ${campaign.id}`.toLocaleLowerCase("en-GB");
    return text.includes(wanted) && (status === "all" || campaign.status === status);
  });

  function clearFilters() { setSearch(""); setStatus("all"); }

  return (
    <section aria-labelledby="hi-campaigns-heading" className="flex flex-col gap-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 id="hi-campaigns-heading" className="type-app-h text-xl font-semibold text-ink">Campaigns</h2>
        </div>
        {campaigns.isSuccess && rows.length > 0 ? (
          <Button variant="ghost" className="min-h-11" aria-label={campaigns.isFetching ? "Refreshing campaigns" : "Refresh campaigns"} disabled={campaigns.isFetching} onClick={() => void campaigns.refetch()}>
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            <span className="hidden sm:inline">{campaigns.isFetching ? "Refreshing…" : "Refresh campaigns"}</span>
          </Button>
        ) : null}
      </div>
      {campaigns.isLoading ? <CampaignBoardSkeleton /> : null}
      {campaigns.isError && !denied ? <ErrorState error={campaigns.error} onRetry={() => void campaigns.refetch()} /> : null}
      {denied ? <EmptyState title="You do not have permission to view these campaigns." body="Ask your RADAR administrator to review campaign access for the selected organisation." /> : null}
      {campaigns.isSuccess && rows.length === 0 ? (
        <EmptyState tone="first-run" title="No campaigns yet" body="Your organisation has no campaigns to view. Review your access or ask your RADAR administrator to assign a campaign."
          action={<Link className="inv-btn min-h-11" to="/high-intent/settings">Review campaign access</Link>} />
      ) : null}
      {campaigns.isSuccess && rows.length > 0 ? (
        <>
          <div className="inv-card flex items-end gap-3 p-4">
            <div className="min-w-0 flex-1"><SearchField label="Find a campaign" placeholder="Find a campaign" value={search} onChange={setSearch} /></div>
            <Select label="Campaign status" value={status} className="min-h-11 w-28 sm:w-44" onChange={(event) => setStatus(event.target.value)}>
              <option value="all">All</option>
              {Array.from(new Set([...rows.map((campaign) => campaign.status), ...(status === "all" ? [] : [status])])).sort().map((value) => <option key={value} value={value}>{value.charAt(0).toUpperCase() + value.slice(1)}</option>)}
            </Select>
          </div>
          <p className="text-xs text-ink-3" aria-live="polite">{visible.length} of {rows.length} campaigns · Live counts from the latest response</p>
          {visible.length === 0 ? (
            <EmptyState tone="filtered" title="No campaigns match your filters" body="Try another name or include every campaign status."
              action={<Button variant="secondary" className="min-h-11" onClick={clearFilters}>Clear filters</Button>} />
          ) : (
            <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {visible.map((campaign) => <li key={campaign.id}><CampaignCard campaign={campaign} /></li>)}
            </ul>
          )}
        </>
      ) : null}
    </section>
  );
}
